import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useContent } from '../../contexts/ContentContext';
import { useTranslation } from '../../i18n';
import useWeddingSlug from '../../hooks/useWeddingSlug';
import BottomFloatingMenu from '../../components/ui/BottomFloatingMenu';
import Icon from '../../components/AppIcon';

const WeddingSchedule = () => {
  const navigate = useNavigate();
  const { weddingPath } = useWeddingSlug();
  const { content } = useContent();
  const { t } = useTranslation();
  const schedule = content?.schedule;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (!schedule?.enabled) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-card rounded-xl p-6 md:p-8 shadow-romantic-md border border-border text-center">
          <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
            <Icon name="Calendar" size={32} color="var(--color-muted-foreground)" />
          </div>
          <h2 className="text-xl md:text-2xl font-semibold text-foreground mb-2">
            {t('schedule.notAvailable')}
          </h2>
          <p className="text-sm md:text-base text-muted-foreground mb-6">
            {t('schedule.notAvailableDesc')}
          </p>
          <button
            onClick={() => navigate(weddingPath())}
            className="text-primary hover:text-primary/80 font-medium transition-romantic"
          >
            {t('schedule.returnHome')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{schedule?.title || t('schedule.title')} - {content?.couple?.names || 'Wedding Invitation'}</title>
        <meta name="description" content="View the schedule for our wedding day celebration" />
      </Helmet>

      <div className="min-h-screen bg-background pb-24">
        <header className="bg-card border-b border-border shadow-romantic-sm sticky top-0 z-50">
          <div className="max-w-screen-xl mx-auto px-4 md:px-6 lg:px-8 py-4 md:py-5">
            <div className="flex items-center justify-between">
              <button
                onClick={() => navigate(-1)}
                className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-romantic press-scale"
                aria-label="Go back"
              >
                <Icon name="ArrowLeft" size={20} />
                <span className="text-sm md:text-base font-medium">{t('nav.back')}</span>
              </button>
              <h1 className="text-lg md:text-xl lg:text-2xl font-semibold text-foreground">
                {schedule?.title || 'Schedule'}
              </h1>
              <div className="w-16" />
            </div>
          </div>
        </header>

        <main className="max-w-4xl mx-auto px-4 md:px-6 lg:px-8 py-8 md:py-12">
          <div className="mb-8 text-center">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <Icon name="Calendar" size={32} color="var(--color-primary)" />
            </div>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground mb-3">
              {schedule?.title || t('schedule.title')}
            </h2>
            <p className="text-sm md:text-base text-muted-foreground max-w-2xl mx-auto">
              {t('schedule.subtitle')}
            </p>
          </div>

          <div className="space-y-6">
            {schedule?.items?.map((item, index) => (
              <div
                key={item.id || index}
                className="bg-card rounded-xl p-5 md:p-6 shadow-romantic-sm border border-border hover:shadow-romantic-md transition-romantic"
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                      <Icon name={item.icon || 'Clock'} size={24} color="var(--color-primary)" />
                    </div>
                  </div>

                  <div className="flex-1">
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-2 mb-2">
                      <h3 className="text-lg md:text-xl font-semibold text-foreground">
                        {item.title}
                      </h3>
                      {item.time && (
                        <span className="text-sm md:text-base font-medium text-primary bg-primary/10 px-3 py-1 rounded-full whitespace-nowrap">
                          {item.time}
                        </span>
                      )}
                    </div>

                    {item.description && (
                      <p className="text-sm md:text-base text-muted-foreground mb-2">
                        {item.description}
                      </p>
                    )}

                    {item.location && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Icon name="MapPin" size={16} />
                        <span>{item.location}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}

            {(!schedule?.items || schedule.items.length === 0) && (
              <div className="text-center py-12">
                <Icon name="Calendar" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
                <p className="text-muted-foreground">{t('schedule.noItems')}</p>
              </div>
            )}
          </div>
        </main>

        <BottomFloatingMenu />
      </div>
    </>
  );
};

export default WeddingSchedule;
